import { XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useHumanTouch } from '@/lib/human-touch'
import { useViewStore } from '@/store/view.store'

const INTERVAL = 5000
const FADE = 600
const WARM_ODDS = 1 / 8

// Mouse + keyboard devices get `desktopTipsKey` when given (no swipes or long-press there).
const isDesktop = () =>
  typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches

export function RotatingTip({
  tipsKey,
  desktopTipsKey,
  values,
}: {
  tipsKey: string
  desktopTipsKey?: string
  values?: Record<string, string>
}) {
  const { t } = useTranslation()
  const [desktop] = useState(isDesktop)
  const tips = t(desktop && desktopTipsKey ? desktopTipsKey : tipsKey, {
    ...values,
    returnObjects: true,
  }) as string[]
  const [index, setIndex] = useState(0)
  const touch = useHumanTouch()
  const [warm, setWarm] = useState<number | null>(null)
  const [visible, setVisible] = useState(true)
  const dismissed = useViewStore((s) => s.tipsDismissed)
  const dismissTips = useViewStore((s) => s.dismissTips)

  useEffect(() => {
    const timer = setInterval(() => {
      setVisible(false)
      setTimeout(() => {
        setWarm((w) =>
          w === null && Math.random() < WARM_ODDS ? Math.floor(Math.random() * touch.count) : null,
        )
        setIndex((i) => (i + 1) % tips.length)
        setVisible(true)
      }, FADE)
    }, INTERVAL)
    return () => clearInterval(timer)
  }, [tips.length, touch.count])

  if (dismissed) return null

  return (
    <span className="flex min-w-0 items-center gap-1">
      <button
        type="button"
        onClick={dismissTips}
        className="shrink-0 cursor-pointer text-muted-foreground/30 hover:text-muted-foreground/60 transition-colors"
      >
        <XIcon className="h-3 w-3" />
      </button>
      <span
        className="min-w-0 truncate text-xs text-muted-foreground/50 italic transition-opacity"
        style={{ opacity: visible ? 1 : 0, transitionDuration: `${FADE}ms` }}
      >
        {warm !== null ? touch.line(warm) : t('common.tip', { tip: tips[index % tips.length] })}
      </span>
    </span>
  )
}
